/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Box, Tooltip as MuiTooltip, useTheme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { IconInfoCircle } from '@tabler/icons-react';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { DateRange } from '../../discover-dashboard-page/types';
import { ComparisonRows } from '../../voice-insights/ComparisonRows';
import {
  dateRangeToTimestamp,
  numbersToStringWithCommas,
  percentageValueAsString,
  stringifyDateRange,
} from '../helpers';
import { useChatsTableRequestParams } from '../hooks/useChatsTableRequestParams';
import useProductMode from '../hooks/useProductMode';
import { OverviewCardInformationSection } from '../overview/OverviewCardInformationSection';
import { ChatTrendsPeriodicalFilter } from '../types';
import InsightCard from './InsightCard';
import PieChartCard from './PieChartCard';
import { RESOLUTION_CHART_COLORS } from './resolutionChartColors';
import { formatNumberHelper } from 'src/components/reusable-components/data-overview-card/helpers';
import { BarGraphWrapper } from 'src/components/solve-insights-charts/bar-graph/barGraphUtils';
import DonutGraph from 'src/components/solve-insights-charts/donut-graph.tsx/DonutGraph';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import {
  useFetchDatasetQuery,
  useGetResolutionInsightsOverviewResolutionComparisonQuery,
  useGetResolutionInsightsOverviewResolutionRateBarChartQuery,
} from 'src/services/insight-cards';
import {
  CustomEventChart,
  DatasetRequest,
  DatasetResponse,
  ResolutionComparisonResponse,
  ResolutionMetric,
  SeriesItem,
} from 'src/services/insight-cards/types';
import type { DonutGraphDataType } from 'src/services/insights/types';
import {
  ChannelMode,
  ConversationChannel,
} from 'src/types/workflowBuilderAPITypes';

export { RESOLUTION_CHART_COLORS } from './resolutionChartColors';

export const RESOLUTION_TOOLTIPS = {
  containedResolutions: 'Conversations resolved by AI, but outcome unverified.',
  emailResolutions:
    'Emails resolved by AI agents: verified (issue confirmed solved), contained (outcome unverified), and pending.',
  overviewAutomatedConversations: 'Conversations resolved by AI agents.',
  resolutions:
    'Conversations resolved by AI agents: verified (issue confirmed solved) and contained (outcome unverified).',
  totalConversations:
    'Total conversations: automated (AI-resolved) and non-automated (human-handled).',
  totalEligibleEmails:
    'Total eligible emails: automated (AI-resolved) and non-automated (human-handled).',
  totalInboundCalls:
    'Total inbound calls: automated (AI-resolved eligible calls) and non-automated (human-handled or not eligible).',
  totalOutboundCalls:
    'Total outbound calls: automated (answered by a human) and non-automated (unanswered or went to voicemail).',
  totalWebCalls:
    'Total web calls: automated (AI-resolved eligible calls) and non-automated (human-handled or not eligible).',
  verifiedResolutions:
    "Conversations resolved by AI agents with the user's issue verified as solved.",
  voiceInboundResolutions:
    'Inbound calls resolved by AI agents: verified (issue confirmed solved) and contained (outcome unverified).',
  webCallResolutions:
    'Web calls resolved by AI agents: verified (issue confirmed solved) and contained (outcome unverified).',
} as const;

type ResolutionType = 'contained' | 'verified' | 'pending';
type AutomationType = 'automated' | 'nonAutomated';

const RESOLUTION_TYPE_BY_LABEL: Record<string, ResolutionType> = {
  Contained: 'contained',
  contained: 'contained',
  'Contained resolutions': 'contained',
  Pending: 'pending',
  pending: 'pending',
  'Pending resolutions': 'pending',
  Verified: 'verified',
  verified: 'verified',
  'Verified resolutions': 'verified',
};

// Legend / donut display order: verified, contained, pending.
const RESOLUTION_TYPE_ORDER: Record<ResolutionType, number> = {
  contained: 1,
  pending: 2,
  verified: 0,
};

const AUTOMATION_TYPE_BY_LABEL: Record<string, AutomationType> = {
  Automated: 'automated',
  False: 'nonAutomated',
  false: 'nonAutomated',
  'Non automated': 'nonAutomated',
  'Non-Automated': 'nonAutomated',
  True: 'automated',
  true: 'automated',
};

export const RESOLUTION_CHART_IDS = {
  conversationsResolutions:
    'solve_resolution_insights_conversations_resolutions',
  conversationsTotalConversations:
    'solve_resolution_insights_conversations_total_conversations',
  overviewAutomatedConversations:
    'solve_resolution_insights_overview_automated_conversations',
  overviewContainedResolutions:
    'solve_resolution_insights_overview_contained_resolutions',
  overviewResolutionComparison:
    'solve_resolution_insights_overview_resolution_comparison',
  overviewResolutionRateTrend:
    'solve_resolution_insights_overview_resolution_rate_trend',
  overviewResolutions: 'solve_resolution_insights_overview_resolutions',
  overviewVerifiedResolutions:
    'solve_resolution_insights_overview_verified_resolutions',
} as const;

export function useIsResolutionUiEnabled() {
  const { data } = useGetFeatureFlagsQuery();
  const featureFlags = data?.feature_flags ?? [];

  return featureFlags.includes('resolution_ui');
}

export function getResolutionChart(
  charts: CustomEventChart[] | undefined,
  chartId: string,
) {
  return charts?.find(chart => chart.chart_id === chartId);
}

export function getDatasetItems(dataset: DatasetResponse | undefined) {
  if (!dataset) {
    return [];
  }

  return dataset.is_grouped ? dataset.series.flat() : dataset.series;
}

export function getDatasetTotal(dataset: DatasetResponse | undefined) {
  return getDatasetItems(dataset).reduce((sum, item) => sum + item.data, 0);
}

export function getResolutionColor(labelOrType: string | null | undefined) {
  const type = getResolutionType(labelOrType ?? '');

  if (type === 'verified') {
    return RESOLUTION_CHART_COLORS.verified;
  }
  if (type === 'pending') {
    return RESOLUTION_CHART_COLORS.nonAutomated;
  }
  return RESOLUTION_CHART_COLORS.contained;
}

function getResolutionType(labelOrType: string): ResolutionType {
  return RESOLUTION_TYPE_BY_LABEL[labelOrType] ?? 'contained';
}

function getResolutionTypeFromItem(item: SeriesItem): ResolutionType {
  return getResolutionType(item.group_value ?? item.label);
}

function getAutomationColor(labelOrType: string) {
  return AUTOMATION_TYPE_BY_LABEL[labelOrType] === 'automated'
    ? RESOLUTION_CHART_COLORS.automated
    : RESOLUTION_CHART_COLORS.nonAutomated;
}

function getAutomationColorFromItem(item: SeriesItem) {
  return getAutomationColor(item.group_value ?? item.label);
}

function sortResolutionItems<
  T extends {
    label: string;
  },
>(a: T, b: T) {
  return (
    RESOLUTION_TYPE_ORDER[getResolutionType(a.label)] -
    RESOLUTION_TYPE_ORDER[getResolutionType(b.label)]
  );
}

function StaticBarSegment({
  color,
  isPrevious = false,
  widthPercentage,
}: {
  color: string;
  isPrevious?: boolean;
  widthPercentage: number;
}) {
  return (
    <Box
      sx={{
        background: isPrevious
          ? `repeating-linear-gradient(
              -45deg,
              ${color},
              ${color} 3px,
              transparent 3px,
              transparent 6px
            )`
          : undefined,
        backgroundColor: isPrevious ? 'transparent' : color,
        borderRadius: '4px',
        display: 'block',
        height: '16px',
        minWidth: widthPercentage > 0 ? '1%' : 0,
        pointerEvents: 'none',
        width: `${widthPercentage}%`,
      }}
    />
  );
}

function HoverableHorizontalSplitMetricSegment({
  color,
  label,
  total,
  value,
  widthPercentage,
}: {
  color: string;
  label: string;
  total: number;
  value: number;
  widthPercentage: number;
}) {
  const { palette } = useTheme();
  const { t } = useTranslation('solve-insights');
  const percentage = total ? value / total : 0;

  return (
    <Box
      sx={{
        minWidth: widthPercentage > 0 ? '1%' : 0,
        width: `${widthPercentage}%`,
      }}
    >
      <MuiTooltip
        placement='top'
        slotProps={{
          popper: {
            sx: [
              {
                '& .MuiTooltip-arrow': {
                  color: palette.colors.grey[800],
                },
                '& .MuiTooltip-tooltip': {
                  backgroundColor: palette.colors.white,
                  maxWidth: 260,
                  padding: '8px 12px',
                  whiteSpace: 'pre-line',
                },
              },
            ],
          },
        }}
        title={
          <Box display='flex' flexDirection='column' gap='4px'>
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {label}
            </Typography>
            <Typography color={palette.colors.black} variant='font14Bold'>
              {numbersToStringWithCommas({ number: value })}
            </Typography>
            <Typography color={palette.colors.black} variant='font14Bold'>
              {numbersToStringWithCommas({
                number: percentage,
                style: 'percent',
              })}{' '}
              <Typography color={palette.colors.grey[600]} variant='font14'>
                {t('cards.tooltip.ofTotal')}
              </Typography>
            </Typography>
          </Box>
        }
      >
        <Box
          sx={{
            '&:hover': {
              filter: 'brightness(0.94)',
            },
            backgroundColor: color,
            borderRadius: '4px',
            display: 'block',
            height: '16px',
            transition: 'filter 120ms ease',
            width: '100%',
          }}
        />
      </MuiTooltip>
    </Box>
  );
}

function HoverableStaticBarSegment({
  changeFromPrevious,
  color,
  currentValue,
  isPrevious = false,
  label,
  percentOfOverall,
  previousValue,
  widthPercentage,
}: {
  changeFromPrevious: number;
  color: string;
  currentValue: number;
  isPrevious?: boolean;
  label: string;
  percentOfOverall: number;
  previousValue: number;
  widthPercentage: number;
}) {
  const { palette } = useTheme();
  const { t } = useTranslation('solve-insights');
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderRadius: '4px',
        cursor: 'default',
        position: 'relative',
        width: `${widthPercentage}%`,
      }}
    >
      <Box
        className='resolution-comparison-tooltip-trigger'
        sx={{
          filter: isHovered ? 'brightness(0.94)' : 'none',
          minWidth: widthPercentage > 0 ? '1%' : 0,
          transition: 'filter 120ms ease',
          width: '100%',
        }}
      >
        <StaticBarSegment
          color={color}
          isPrevious={isPrevious}
          widthPercentage={100}
        />
      </Box>
      {widthPercentage > 0 && (
        <Box
          className='resolution-comparison-tooltip'
          sx={{
            backgroundColor: palette.colors.white,
            border: `1px solid ${palette.colors.slate[200]}`,
            bottom: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            left: '50%',
            minWidth: '226px',
            opacity: isHovered ? 1 : 0,
            padding: '8px',
            pointerEvents: 'none',
            position: 'absolute',
            transform: isHovered
              ? 'translate(-50%, -8px)'
              : 'translate(-50%, 0)',
            transition: 'opacity 120ms ease, transform 120ms ease',
            visibility: isHovered ? 'visible' : 'hidden',
            zIndex: 2,
          }}
        >
          <Typography color={palette.colors.black} variant='font14Bold'>
            {label}
          </Typography>
          <Box alignItems='flex-start' display='flex'>
            <Box
              sx={{
                backgroundColor: color,
                borderRadius: '2px',
                height: '12px',
                width: '12px',
              }}
            />
            <Box
              display='flex'
              flexDirection='column'
              gap='4px'
              marginLeft='8px'
            >
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {label}
              </Typography>
              <Typography color={palette.colors.black} variant='font14Bold'>
                {numbersToStringWithCommas({ number: currentValue })}{' '}
                {t('resolution.resolutionsUnit')}
              </Typography>
              <Typography color={palette.colors.black} variant='font14Bold'>
                {numbersToStringWithCommas({
                  number: percentOfOverall,
                  style: 'percent',
                })}{' '}
                <Typography color={palette.colors.grey[600]} variant='font14'>
                  {t('resolution.ofOverall')}
                </Typography>
              </Typography>
              <Typography color={palette.colors.black} variant='font14Bold'>
                {numbersToStringWithCommas({
                  number: changeFromPrevious,
                  style: 'percent',
                })}{' '}
                <Typography color={palette.colors.grey[600]} variant='font14'>
                  {t('common.chatsSavingsCard.fromPreviousPeriod')}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box display='flex'>
            <Box
              sx={{
                background: `repeating-linear-gradient(
                  -45deg,
                  ${color},
                  ${color} 3px,
                  ${palette.colors.primary[100]} 3px,
                  ${palette.colors.primary[100]} 6px
                )`,
                borderRadius: '2px',
                height: '12px',
                width: '12px',
              }}
            />
            <Box
              display='flex'
              flexDirection='column'
              gap='4px'
              marginLeft='8px'
            >
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {label}
              </Typography>
              <Typography color={palette.colors.black} variant='font14Bold'>
                {numbersToStringWithCommas({ number: previousValue })}{' '}
                {t('resolution.resolutionsUnit')}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function buildDatasetRequest({
  channels,
  chart,
  chatsTableRequestParams,
  endTimestamp,
  forceLineChart,
  startTimestamp,
}: {
  chart: CustomEventChart | undefined;
  channels: ConversationChannel[];
  chatsTableRequestParams?: DatasetRequest['chats_table_request'];
  endTimestamp: number;
  forceLineChart?: boolean;
  startTimestamp: number;
}): DatasetRequest | typeof skipToken {
  if (!chart) {
    return skipToken;
  }

  return {
    aggregation_type: chart.aggregation_type,
    channels,
    ...(forceLineChart || chart.chart_type === 'line'
      ? { chart_type: 'line' }
      : {}),
    ...(chatsTableRequestParams
      ? { chats_table_request: chatsTableRequestParams }
      : {}),
    derived_metric: chart.derived_metric,
    end_timestamp: endTimestamp,
    formula_text: chart.formula_text,
    group_by: chart.group_by,
    measurement_type: chart.measurement_type,
    properties: chart.properties,
    start_timestamp: startTimestamp,
  };
}

function getChatsTableRequest(
  requestParams: ReturnType<typeof useChatsTableRequestParams>,
): DatasetRequest['chats_table_request'] | undefined {
  return 'chats_table_request' in requestParams
    ? (requestParams.chats_table_request as DatasetRequest['chats_table_request'])
    : undefined;
}

function getProductModeChannels(
  productMode: ReturnType<typeof useProductMode>[0],
): ConversationChannel[] {
  if (productMode === 'api' || productMode === 'slack') {
    return [productMode];
  }

  return ['widget'];
}

export function useResolutionConversationChannels() {
  const [productMode] = useProductMode();
  return getProductModeChannels(productMode);
}

export function ResolutionOverviewMetricCard({
  isLoading,
  metric,
  showPercentage,
  title,
  tooltip,
}: {
  isLoading: boolean;
  metric: ResolutionMetric | undefined;
  showPercentage?: boolean;
  title: string;
  tooltip?: string;
}) {
  const { palette } = useTheme();
  const percentage =
    showPercentage &&
    metric?.percentage !== undefined &&
    metric.percentage !== null
      ? numbersToStringWithCommas({
          number: metric.percentage,
          style: 'percent',
        })
      : null;
  const value = metric
    ? `${numbersToStringWithCommas({ number: metric.value })}${
        showPercentage && percentage ? ` (${percentage})` : ''
      }`
    : '0';

  return (
    <Box
      border={'1px solid ' + palette.colors.grey[200]}
      borderRadius='8px'
      display='flex'
      flexDirection='column'
      gap='20px'
      minWidth='308px'
      p={3}
    >
      <Box alignItems='center' display='flex' gap='4px'>
        <Typography noWrap variant='font14Bold'>
          {title}
        </Typography>
        {tooltip && (
          <Tooltip tooltipContent={tooltip}>
            <IconInfoCircle color={palette.grey[700]} size={16} />
          </Tooltip>
        )}
      </Box>
      {isLoading ? (
        <Skeleton height='42px' width='150px' />
      ) : (
        <Box lineHeight='42px'>
          <Typography color={palette.colors.black} noWrap variant='font32'>
            {value}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export function ResolutionTotalConversationsCard({
  chart,
  dateRange,
  tooltip,
}: {
  chart: CustomEventChart | undefined;
  dateRange: DateRange;
  tooltip?: string;
}) {
  const { t } = useTranslation('solve-insights');
  const channels = useResolutionConversationChannels();
  const chatsTableRequestParams = useChatsTableRequestParams(channels);
  const timestamps = dateRangeToTimestamp(dateRange);
  const { data, isFetching, isLoading } = useFetchDatasetQuery(
    buildDatasetRequest({
      channels,
      chart,
      chatsTableRequestParams: getChatsTableRequest(chatsTableRequestParams),
      endTimestamp: timestamps.end_timestamp,
      startTimestamp: timestamps.start_timestamp,
    }),
  );

  return (
    <HorizontalSplitMetricCard
      dataset={data}
      isFetching={isFetching}
      isLoading={isLoading}
      labels={{
        false: 'Non automated',
        true: 'Automated',
      }}
      title={chart?.name ?? t('resolution.totalConversations')}
      tooltip={tooltip ?? chart?.description ?? 'TBD'}
    />
  );
}

export function AutomatedConversationsFromCountsCard({
  automatedConversations,
  isFetching = false,
  isLoading,
  title,
  tooltip = 'TBD',
  totalConversations,
}: {
  automatedConversations: number;
  isFetching?: boolean;
  isLoading: boolean;
  title: string;
  tooltip?: string;
  totalConversations: number;
}) {
  const automatedCount = Math.max(automatedConversations, 0);
  const nonAutomatedCount = Math.max(totalConversations - automatedCount, 0);
  const dataset: DatasetResponse = {
    chart_type: 'bar',
    is_grouped: true,
    series: [
      [
        {
          aggregation_label: null,
          data: automatedCount,
          event_name: null,
          group_by_label: null,
          // lookup key (AUTOMATION_TYPE_BY_LABEL) and comparison value, not display copy
          label: 'Automated',
          percentage: totalConversations
            ? automatedCount / totalConversations
            : 0,
          percentage_baseline: 0,
        },
        {
          aggregation_label: null,
          data: nonAutomatedCount,
          event_name: null,
          group_by_label: null,
          // lookup key (AUTOMATION_TYPE_BY_LABEL) and comparison value, not display copy
          label: 'Non automated',
          percentage: totalConversations
            ? nonAutomatedCount / totalConversations
            : 0,
          percentage_baseline: 0,
        },
      ],
    ],
    total_sum: totalConversations,
    x_values: ['Automated', 'Non automated'],
  };

  return (
    <HorizontalSplitMetricCard
      dataset={dataset}
      isFetching={isFetching}
      isLoading={isLoading}
      labels={{}}
      title={title}
      tooltip={tooltip}
    />
  );
}

export function AutomatedConversationsCard({
  channelMode,
  channels,
  chart,
  dateRange,
  isTotalLoading,
  title,
  tooltip,
  totalConversations,
}: {
  channelMode: ChannelMode;
  channels: ConversationChannel[];
  chart?: CustomEventChart;
  dateRange: DateRange;
  isTotalLoading: boolean;
  title: string;
  totalConversations: number;
  tooltip?: string;
}) {
  const chatsTableRequestParams = useChatsTableRequestParams(channels);
  const chatsTableRequest = getChatsTableRequest(chatsTableRequestParams);
  const chatsTableRequestWithChannelMode: DatasetRequest['chats_table_request'] =
    chatsTableRequest
      ? { ...chatsTableRequest, channel_mode: channelMode }
      : undefined;
  const timestamps = dateRangeToTimestamp(dateRange);
  const { data, isFetching, isLoading } = useFetchDatasetQuery(
    buildDatasetRequest({
      channels,
      chart,
      chatsTableRequestParams: chatsTableRequestWithChannelMode,
      endTimestamp: timestamps.end_timestamp,
      startTimestamp: timestamps.start_timestamp,
    }),
  );

  return (
    <AutomatedConversationsFromCountsCard
      automatedConversations={getDatasetTotal(data)}
      isFetching={isFetching}
      isLoading={isTotalLoading || isLoading}
      title={title}
      tooltip={tooltip ?? chart?.description ?? 'TBD'}
      totalConversations={totalConversations}
    />
  );
}

export function ResolutionBreakdownCard({
  channelMode,
  channels: channelOverride,
  chart,
  dateRange,
  includePrevious = false,
  tooltip,
}: {
  channelMode?: ChannelMode;
  channels?: ConversationChannel[];
  chart?: CustomEventChart;
  dateRange: DateRange;
  includePrevious?: boolean;
  tooltip?: string;
}) {
  const { t } = useTranslation('solve-insights');
  const fallbackChannels = useResolutionConversationChannels();
  const channels =
    channelOverride ??
    (chart?.channels?.length ? chart.channels : fallbackChannels);
  const chatsTableRequestParams = useChatsTableRequestParams(channels);
  const chatsTableRequest = getChatsTableRequest(chatsTableRequestParams);
  const chatsTableRequestWithChannelMode: DatasetRequest['chats_table_request'] =
    channelMode && chatsTableRequest
      ? { ...chatsTableRequest, channel_mode: channelMode }
      : chatsTableRequest;
  const timestamps = dateRangeToTimestamp(dateRange);
  const {
    data: comparisonData,
    isFetching: isComparisonFetching,
    isLoading: isComparisonLoading,
  } = useGetResolutionInsightsOverviewResolutionComparisonQuery(
    {
      end: timestamps.end_timestamp,
      start: timestamps.start_timestamp,
    },
    { skip: !includePrevious },
  );
  const { data, isFetching, isLoading } = useFetchDatasetQuery(
    includePrevious
      ? skipToken
      : buildDatasetRequest({
          channels,
          chart,
          chatsTableRequestParams:
            chart?.channels?.length && !channelMode
              ? undefined
              : chatsTableRequestWithChannelMode,
          endTimestamp: timestamps.end_timestamp,
          startTimestamp: timestamps.start_timestamp,
        }),
  );

  if (includePrevious) {
    return (
      <ResolutionComparisonCard
        comparisonData={comparisonData}
        dateRange={dateRange}
        isFetching={isComparisonFetching}
        isLoading={isComparisonLoading}
        title={chart?.name ?? t('resolution.resolutions')}
        tooltip={tooltip ?? RESOLUTION_TOOLTIPS.resolutions}
      />
    );
  }

  return (
    <ResolutionDonutCard
      dataset={data}
      dateRange={dateRange}
      isFetching={isFetching}
      isLoading={isLoading}
      title={chart?.name ?? t('resolution.resolutions')}
      tooltip={tooltip ?? chart?.description ?? 'TBD'}
    />
  );
}

export function ResolutionBreakdownFromCountsCard({
  containedResolutions,
  dateRange,
  isLoading,
  pendingResolutions = 0,
  title = 'Resolutions',
  tooltip = 'TBD',
  verifiedResolutions,
}: {
  containedResolutions: number;
  dateRange: DateRange;
  isLoading: boolean;
  pendingResolutions?: number;
  title?: string;
  tooltip?: string;
  verifiedResolutions: number;
}) {
  const total = verifiedResolutions + containedResolutions + pendingResolutions;
  const dataset: DatasetResponse = {
    chart_type: 'pie',
    is_grouped: false,
    series: [
      {
        aggregation_label: null,
        data: verifiedResolutions,
        event_name: null,
        group_by_label: null,
        // lookup key (RESOLUTION_TYPE_BY_LABEL) and comparison value, not display copy
        label: 'Verified',
        percentage: total ? verifiedResolutions / total : 0,
        percentage_baseline: 0,
      },
      {
        aggregation_label: null,
        data: containedResolutions,
        event_name: null,
        group_by_label: null,
        // lookup key (RESOLUTION_TYPE_BY_LABEL) and comparison value, not display copy
        label: 'Contained',
        percentage: total ? containedResolutions / total : 0,
        percentage_baseline: 0,
      },
      {
        aggregation_label: null,
        data: pendingResolutions,
        event_name: null,
        group_by_label: null,
        // lookup key (RESOLUTION_TYPE_BY_LABEL) and comparison value, not display copy
        label: 'Pending',
        percentage: total ? pendingResolutions / total : 0,
        percentage_baseline: 0,
      },
    ],
    total_sum: total,
    x_values: [],
  };

  return (
    <ResolutionDonutCard
      dataset={dataset}
      dateRange={dateRange}
      isFetching={false}
      isLoading={isLoading}
      title={title}
      tooltip={tooltip}
    />
  );
}

const RESOLUTION_LABEL_BY_TYPE: Record<ResolutionType, string> = {
  contained: 'Contained',
  pending: 'Pending',
  verified: 'Verified',
};

function getResolutionRows(dataset: DatasetResponse | undefined) {
  const rowsByType = getDatasetItems(dataset).reduce(
    (acc, item) => {
      const type = getResolutionTypeFromItem(item);
      const existing = acc[type];

      return {
        ...acc,
        [type]: {
          aggregation_label:
            existing?.aggregation_label ?? item.aggregation_label,
          data: (existing?.data ?? 0) + item.data,
          event_name: existing?.event_name ?? item.event_name,
          group_by_label: existing?.group_by_label ?? item.group_by_label,
          label: RESOLUTION_LABEL_BY_TYPE[type],
          percentage: 0,
          percentage_baseline: 0,
        },
      };
    },
    {} as Record<ResolutionType, ReturnType<typeof getDatasetItems>[number]>,
  );

  return Object.values(rowsByType).sort(sortResolutionItems);
}

function HorizontalSplitMetricCard({
  dataset,
  isFetching,
  isLoading,
  labels,
  title,
  tooltip,
}: {
  dataset: DatasetResponse | undefined;
  isFetching: boolean;
  isLoading: boolean;
  labels: Record<string, string>;
  title: string;
  tooltip: string;
}) {
  const { palette } = useTheme();
  const rows = getDatasetItems(dataset).map(item => ({
    ...item,
    label: labels[item.label] ?? labels[item.label.toLowerCase()] ?? item.label,
  }));
  const total = rows.reduce((sum, item) => sum + item.data, 0);
  const sortedRows = rows.sort((a, b) => {
    if (a.label === 'Automated') {
      return -1;
    }
    if (b.label === 'Automated') {
      return 1;
    }
    return 0;
  });
  const displayPercentages =
    total && sortedRows.length === 2
      ? (() => {
          const firstPercentage =
            Math.round((sortedRows[0].data / total) * 100) / 100;
          return [firstPercentage, 1 - firstPercentage];
        })()
      : sortedRows.map(item => (total ? item.data / total : 0));

  return (
    <InsightCard
      isFetching={isFetching}
      isLoading={isLoading}
      justifyContent='flex-start'
      title={title}
      tooltip={
        <Tooltip tooltipContent={tooltip}>
          <IconInfoCircle color={palette.grey[700]} size={16} />
        </Tooltip>
      }
    >
      <Box display='flex' flexDirection='column' gap='24px' marginTop='8px'>
        <Typography variant='font32'>
          {numbersToStringWithCommas({ number: total })}
        </Typography>
        <Box display='flex' gap='1px'>
          {sortedRows.map(item => {
            const widthPercentage = total ? (item.data / total) * 100 : 0;

            return (
              <HoverableHorizontalSplitMetricSegment
                color={getAutomationColorFromItem(item)}
                key={item.label}
                label={item.label}
                total={total}
                value={item.data}
                widthPercentage={widthPercentage}
              />
            );
          })}
        </Box>
        <ComparisonRows
          rows={sortedRows.map((item, index) => ({
            color: getAutomationColorFromItem(item),
            firstColumnText: numbersToStringWithCommas({ number: item.data }),
            label: item.label,
            secondColumnText: total
              ? numbersToStringWithCommas({
                  number: displayPercentages[index],
                  style: 'percent',
                })
              : '0%',
          }))}
        />
      </Box>
    </InsightCard>
  );
}

function ResolutionDonutCard({
  dataset,
  dateRange,
  isFetching,
  isLoading,
  title,
  tooltip,
}: {
  dateRange: DateRange;
  dataset: DatasetResponse | undefined;
  isFetching: boolean;
  isLoading: boolean;
  title: string;
  tooltip: string;
}) {
  const rows = useMemo(() => {
    const datasetRows = getResolutionRows(dataset)
      .filter(item => item.data > 0)
      .sort(sortResolutionItems);

    return datasetRows.length
      ? datasetRows
      : [
          {
            aggregation_label: null,
            data: 0,
            event_name: null,
            group_by_label: null,
            // lookup key (RESOLUTION_TYPE_BY_LABEL) and comparison value, not display copy
            label: 'Verified',
            percentage: 0,
            percentage_baseline: 0,
          },
          {
            aggregation_label: null,
            data: 0,
            event_name: null,
            group_by_label: null,
            // lookup key (RESOLUTION_TYPE_BY_LABEL) and comparison value, not display copy
            label: 'Contained',
            percentage: 0,
            percentage_baseline: 0,
          },
        ];
  }, [dataset]);
  const total = rows.reduce((sum, item) => sum + item.data, 0);
  const verified = rows.find(
    item => getResolutionType(item.label) === 'verified',
  );
  const verifiedRate = total ? (verified?.data ?? 0) / total : 0;
  const donutData = useMemo<DonutGraphDataType>(
    () => ({
      values: rows.map(item => ({
        extended_label: null,
        label: item.label,
        type: 'percentage',
        value: item.data,
      })),
    }),
    [rows],
  );
  const centerText = total
    ? numbersToStringWithCommas({
        number: verifiedRate,
        style: 'percent',
      })
    : '0%';

  return (
    <PieChartCard
      centerSubText=''
      centerText={centerText}
      colors={rows.map(item => getResolutionColor(item.label))}
      dateRange={dateRange}
      donutData={donutData}
      isLoading={isLoading || isFetching}
      title={title}
      tooltip={tooltip}
    />
  );
}

function ResolutionComparisonCard({
  comparisonData,
  dateRange,
  isFetching,
  isLoading,
  title,
  tooltip,
}: {
  comparisonData: ResolutionComparisonResponse | undefined;
  dateRange: DateRange;
  isFetching: boolean;
  isLoading: boolean;
  title: string;
  tooltip: string;
}) {
  const { palette } = useTheme();
  const rows = comparisonData?.rows.length
    ? comparisonData.rows
    : [
        {
          change_from_previous_period: 0,
          current_value: 0,
          label: 'Verified' as const,
          percentage_of_overall: 0,
          previous_value: 0,
          previous_width_percentage: 0,
          width_percentage: 0,
        },
        {
          change_from_previous_period: 0,
          current_value: 0,
          label: 'Contained' as const,
          percentage_of_overall: 0,
          previous_value: 0,
          previous_width_percentage: 0,
          width_percentage: 0,
        },
      ];
  const verifiedRate = comparisonData?.verified_rate ?? 0;
  const rateDelta = comparisonData?.verified_rate_delta ?? 0;
  const donutData: DonutGraphDataType = {
    values: rows.map(item => ({
      extended_label: null,
      label: item.label,
      type: 'percentage',
      value: item.current_value,
    })),
  };

  return (
    <InsightCard
      headerRightText={stringifyDateRange(dateRange)}
      isFetching={isFetching}
      isLoading={isLoading}
      justifyContent='space-between'
      title={title}
      tooltip={
        <Tooltip tooltipContent={tooltip}>
          <IconInfoCircle color={palette.grey[700]} size={16} />
        </Tooltip>
      }
    >
      <Box display='flex' flexDirection='column' gap='16px' marginTop='8px'>
        <Box
          sx={{
            alignItems: 'center',
            display: 'grid',
            gap: '36px',
            gridTemplateColumns: '190px 1fr',
            minHeight: '196px',
          }}
        >
          <Box
            sx={{
              height: '180px',
              position: 'relative',
              width: '180px',
            }}
          >
            <DonutGraph
              centerSubText=''
              centerText=' '
              colors={rows.map(item => getResolutionColor(item.label))}
              containerProps={{
                style: { height: '180px', width: '180px' },
              }}
              data={donutData}
              disableTooltip
            />
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                left: '50%',
                pointerEvents: 'none',
                position: 'absolute',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Typography color={palette.colors.black} variant='font32'>
                {numbersToStringWithCommas({
                  number: verifiedRate,
                  style: 'percent',
                })}
              </Typography>
              <Typography
                color={
                  rateDelta < 0
                    ? palette.colors.red[500]
                    : palette.colors.green[500]
                }
                variant='font14Bold'
              >
                {rateDelta === 0 ? '0%' : percentageValueAsString(rateDelta)}
              </Typography>
            </Box>
          </Box>
          <Box display='flex' flexDirection='column' gap='20px'>
            {rows.map(row => {
              const color = getResolutionColor(row.label);

              return (
                <Box
                  alignItems='center'
                  display='grid'
                  gap='12px'
                  gridTemplateColumns='72px 1fr 46px'
                  key={row.label}
                >
                  <Box textAlign='right'>
                    <Typography variant='font12'>{row.label}</Typography>
                  </Box>
                  <Box display='flex' flexDirection='column' gap='6px'>
                    <HoverableStaticBarSegment
                      changeFromPrevious={row.change_from_previous_period}
                      color={color}
                      currentValue={row.current_value}
                      label={row.label}
                      percentOfOverall={row.percentage_of_overall}
                      previousValue={row.previous_value}
                      widthPercentage={row.width_percentage}
                    />
                    <HoverableStaticBarSegment
                      changeFromPrevious={row.change_from_previous_period}
                      color={color}
                      currentValue={row.current_value}
                      isPrevious
                      label={row.label}
                      percentOfOverall={row.percentage_of_overall}
                      previousValue={row.previous_value}
                      widthPercentage={row.previous_width_percentage}
                    />
                  </Box>
                  <Box display='flex' flexDirection='column' gap='6px'>
                    <Typography variant='font12'>
                      {numbersToStringWithCommas({ number: row.current_value })}
                    </Typography>
                    <Typography variant='font12'>
                      {numbersToStringWithCommas({
                        number: row.previous_value,
                      })}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <OverviewCardInformationSection />
    </InsightCard>
  );
}

export function ResolutionRateTrendCard({
  dateRange,
  periodicalFilter,
}: {
  dateRange: DateRange;
  periodicalFilter: ChatTrendsPeriodicalFilter;
}) {
  const { palette } = useTheme();
  const { t } = useTranslation('solve-insights');
  const timestamps = dateRangeToTimestamp(dateRange);
  const { data, isFetching, isLoading } =
    useGetResolutionInsightsOverviewResolutionRateBarChartQuery({
      end: timestamps.end_timestamp,
      period: periodicalFilter,
      start: timestamps.start_timestamp,
    });

  if (isLoading || isFetching) {
    return <Skeleton height='344px' width='100%' />;
  }

  return (
    <Box
      sx={{
        border: `1px solid ${palette.colors.slate[200]}`,
        borderRadius: '8px',
        flex: '1 1 0px',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '4px',
          padding: '24px 0px 0px 24px',
        }}
      >
        <Typography variant='font16Bold'>
          {t('resolution.resolutionRate')}
        </Typography>
        <Tooltip tooltipContent={RESOLUTION_TOOLTIPS.resolutions}>
          <IconInfoCircle color='currentColor' height={16} width={16} />
        </Tooltip>
      </Box>
      {data?.bar.values.length ? (
        <BarGraphWrapper data={data} palette={palette} />
      ) : (
        <Box height='300px' />
      )}
    </Box>
  );
}

function getEmailReceivedSegments({
  eligibleEmails,
  emailsReceived,
}: {
  eligibleEmails: number;
  emailsReceived: number;
}) {
  const ineligible = Math.max(emailsReceived - eligibleEmails, 0);
  const eligible = Math.min(eligibleEmails, emailsReceived);

  return { eligible, ineligible };
}

export function EmailsReceivedCard({
  eligibleEmails,
  emailsReceived,
  isLoading,
}: {
  eligibleEmails: number;
  emailsReceived: number;
  isLoading: boolean;
}) {
  const { t } = useTranslation('solve-insights');
  const { palette } = useTheme();

  const { eligible, ineligible } = getEmailReceivedSegments({
    eligibleEmails,
    emailsReceived,
  });

  const colors = {
    eligible: palette.colors.blue[400],
    ineligible: palette.colors.slate[400],
  };

  const rows = [
    {
      color: colors.eligible,
      count: eligible,
      label: t('common.emailMetrics.eligible'),
    },
    {
      color: colors.ineligible,
      count: ineligible,
      label: t('common.emailMetrics.ineligible'),
    },
  ];

  return (
    <InsightCard
      isFetching={isLoading}
      isLoading={isLoading}
      justifyContent='flex-start'
      title={t('common.emailMetrics.emailsReceived')}
      tooltip={
        <Tooltip
          tooltipContent={t('common.emailMetrics.emailsReceivedTooltip')}
        >
          <IconInfoCircle color={palette.grey[700]} size={16} />
        </Tooltip>
      }
    >
      <Box display='flex' flexDirection='column' gap='24px' marginTop='8px'>
        <Typography variant='font32'>
          {numbersToStringWithCommas({ number: emailsReceived })}
        </Typography>
        {emailsReceived > 0 && (
          <Box display='flex' gap='1px'>
            {rows.map(row => {
              const widthPercentage = (row.count / emailsReceived) * 100;

              return (
                <Box
                  key={row.label}
                  sx={{
                    backgroundColor: row.color,
                    borderRadius: '4px',
                    height: '16px',
                    minWidth: widthPercentage > 0 ? '1%' : 0,
                    width: `${widthPercentage}%`,
                  }}
                />
              );
            })}
          </Box>
        )}
        <ComparisonRows
          rows={rows.map(row => ({
            color: row.color,
            firstColumnText: numbersToStringWithCommas({ number: row.count }),
            label: row.label,
            secondColumnText: emailsReceived
              ? numbersToStringWithCommas({
                  number: row.count / emailsReceived,
                  style: 'percent',
                })
              : '0%',
          }))}
        />
      </Box>
    </InsightCard>
  );
}

export function ResponseRateCard({
  isLoading,
  responseRate,
  responsesSent,
}: {
  isLoading: boolean;
  responseRate: number;
  responsesSent: number;
}) {
  const { t } = useTranslation('solve-insights');
  const { palette } = useTheme();
  const clampedRate = Math.min(Math.max(responseRate, 0), 1);
  const widthPercentage = clampedRate * 100;

  return (
    <InsightCard
      isFetching={isLoading}
      isLoading={isLoading}
      justifyContent='flex-start'
      title={t('common.emailMetrics.responsesSent')}
      tooltip={
        <Tooltip tooltipContent={t('common.emailMetrics.responsesSentTooltip')}>
          <IconInfoCircle color={palette.grey[700]} size={16} />
        </Tooltip>
      }
    >
      <Box display='flex' flexDirection='column' gap='24px' marginTop='8px'>
        <Typography variant='font32'>
          {t('common.emailMetrics.responsesSentWithRate', {
            rate: formatNumberHelper('response_rate', responseRate),
            sentCount: numbersToStringWithCommas({ number: responsesSent }),
          })}
        </Typography>
        <Box
          sx={{
            backgroundColor: palette.colors.slate[200],
            borderRadius: '4px',
            height: '16px',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: palette.colors.teal[500],
              borderRadius: '4px',
              height: '100%',
              minWidth: widthPercentage > 0 ? '1%' : 0,
              width: `${widthPercentage}%`,
            }}
          />
        </Box>
      </Box>
    </InsightCard>
  );
}
